import { ExternalLink } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Combobox, type ComboboxOption } from '@/components/ui/combobox'
import { cn } from '@/lib/utils'
import {
  MES_PRODUCT_SECTIONS,
  isOptionField,
  mirrorStorageText,
  qualiosHref,
  type MesProductDraft,
  type MesProductFieldDef,
  type MesProductFormErrors,
} from '@/lib/mesProducts'
import type { MesProductReference, MesProductStorageUnit } from '@/types/cgt'

/**
 * The Product Code's 24 fields (z8nrz7fvhx), in the four sections of the spec's
 * Product Information mock, side by side on a wide screen. Configure → Product
 * Code and the dialog an MES run opens both render this, so they cannot differ.
 *
 * The code is editable only while adding: it never changes after that.
 *
 * Each field's label is blue once the field holds a value and light grey while
 * it is empty, so a label never reads as an entry. An empty field's box is
 * light grey too, so what is still to fill stands out at a glance ("None" and
 * "No" are empty values, so they count).
 */
export function MesProductForm({
  draft,
  onChange,
  reference,
  errors = {},
  readOnly = false,
  creating = false,
  knownUnits = [],
}: {
  draft: MesProductDraft
  onChange: (next: MesProductDraft) => void
  reference: MesProductReference | undefined
  errors?: MesProductFormErrors
  readOnly?: boolean
  creating?: boolean
  /** The product's own storage units, so they read even without the reference lists. */
  knownUnits?: MesProductStorageUnit[]
}) {
  const set = (key: keyof MesProductDraft, value: string) => onChange({ ...draft, [key]: value })
  const adrOther = reference?.adrIataOther ?? 'Other'

  return (
    <div className='grid gap-x-6 gap-y-6 md:grid-cols-2 xl:grid-cols-4'>
      {MES_PRODUCT_SECTIONS.map(section => (
        <section key={section.title} className='space-y-3'>
          <h3 className='text-xs font-semibold uppercase tracking-wide text-primary'>{section.title}</h3>
          {section.fields.map(field => (
            <div key={field.key} className='space-y-1'>
              <Label
                htmlFor={`mp-${field.key}`}
                className={cn('text-xs font-semibold', draft[field.key] ? 'text-[#1a6dc8]' : 'text-gray-400')}
              >
                {field.label}
                {field.key === 'code' && creating && <span className='text-destructive'> *</span>}
              </Label>
              <div className={cn(!draft[field.key] && '[&_input]:bg-gray-100')}>
                <FieldControl
                  field={field}
                  draft={draft}
                  reference={reference}
                  knownUnits={knownUnits}
                  set={set}
                  disabled={
                    readOnly ||
                    (field.key === 'code' && !creating) ||
                    (field.key === 'adrIataClassificationOther' && draft.adrIataClassification !== adrOther)
                  }
                />
              </div>
              {errors[field.key] && <p className='text-xs text-destructive'>{errors[field.key]}</p>}
            </div>
          ))}
        </section>
      ))}
    </div>
  )
}

function FieldControl({
  field,
  draft,
  reference,
  knownUnits,
  set,
  disabled,
}: {
  field: MesProductFieldDef
  draft: MesProductDraft
  reference: MesProductReference | undefined
  knownUnits: MesProductStorageUnit[]
  set: (key: keyof MesProductDraft, value: string) => void
  disabled: boolean
}) {
  const value = draft[field.key]
  const id = `mp-${field.key}`

  // Combobox, not SearchableSelect: its list renders inside the dialog, so
  // typing reaches its search. It has no disabled state, so a field that
  // cannot be changed shows its value in a disabled box instead.
  if (isOptionField(field.key)) {
    if (disabled) return <Input id={id} value={value} disabled title={value || undefined} />
    const list = reference?.lists.find(l => l.field === field.key)
    const options: ComboboxOption[] = [
      { value: '', label: 'None' },
      ...(list?.options ?? []).map(label => ({ value: label, label })),
      // A value whose option has since left the list still reads and stays put.
      ...(value && !list?.options.includes(value) ? [{ value, label: value }] : []),
    ]
    return (
      <Combobox
        id={id}
        value={value}
        onChange={v => set(field.key, v)}
        options={options}
        placeholder='None'
        title={
          list && list.options.length === 0
            ? `No options yet: add them in Configure → Product Code → Option Sets → ${list.name}.`
            : field.label
        }
      />
    )
  }

  if (field.kind === 'storage') {
    const listed = reference?.storageUnits ?? []
    const units = [...listed, ...knownUnits.filter(k => !listed.some(u => u.id === k.id))]
    const mirror = field.key === 'mirrorStorageUnitId'
    const text = (unitId: string) => units.find(u => u.id === unitId)?.text ?? ''
    if (disabled) {
      const shown = mirror ? mirrorStorageText(text(value) || null) : text(value)
      return <Input id={id} value={shown} disabled title={shown || undefined} />
    }
    const options: ComboboxOption[] = mirror
      ? [
          { value: '', label: 'No' },
          ...units
            .filter(u => u.id !== draft.dedicatedStorageUnitId)
            .map(u => ({ value: u.id, label: mirrorStorageText(u.text), keywords: [u.label] })),
        ]
      : [{ value: '', label: 'None' }, ...units.map(u => ({ value: u.id, label: u.text, keywords: [u.label] }))]
    return (
      <Combobox
        id={id}
        value={value}
        onChange={v => set(field.key, v)}
        options={options}
        placeholder={mirror ? 'No' : 'None'}
        title={field.label}
      />
    )
  }

  if (field.key === 'qualiosLink') {
    const href = qualiosHref(value)
    return (
      <div className='flex items-center gap-2'>
        {disabled && href ? (
          <a
            href={href}
            target='_blank'
            rel='noopener noreferrer'
            className='inline-flex min-h-9 min-w-0 flex-1 items-center gap-1 truncate rounded-md border px-3 text-sm text-primary underline'
            title={value}
          >
            <span className='truncate'>{value}</span>
            <ExternalLink className='h-3.5 w-3.5 shrink-0' />
          </a>
        ) : (
          <Input id={id} value={value} disabled={disabled} onChange={e => set(field.key, e.target.value)} />
        )}
        {!disabled && href && (
          <a href={href} target='_blank' rel='noopener noreferrer' title='Open link' className='text-primary'>
            <ExternalLink className='h-4 w-4' />
          </a>
        )}
      </div>
    )
  }

  return (
    <Input
      id={id}
      value={value}
      disabled={disabled}
      inputMode={field.kind === 'number' ? 'decimal' : undefined}
      onChange={e => set(field.key, e.target.value)}
      title={value || undefined}
    />
  )
}
